import type { Quote, QuoteTemplate } from '../types';
import type { createQuote } from './crmService';
import { calculateQuoteTotals } from './quoteLogic';

/**
 * Anlegen eines Angebots in EINEM Insert statt `createQuote` + `updateQuote`.
 *
 * Vorher (Audit 24.09., Q7) zog `createQuote` die Nummer und legte ein leeres
 * Angebot an; Vorlage bzw. eingelesener Beleg kamen erst mit einem zweiten
 * `updateQuote`. Schlug der fehl, blieb ein leeres, nummeriertes Angebot
 * zurueck und der KI-Import war verloren. Jetzt traegt schon der Insert alle
 * Felder, die `createQuote` annimmt (Positionen, Details, Texte, Rabatt,
 * Steuer). Was er (noch) nicht annimmt, bleibt als `rest` fuer einen
 * Nachtrag — dessen Scheitern kostet dann keine Inhalte mehr.
 */
export type CreateQuoteInput = Parameters<typeof createQuote>[0];

/** Felder, die `createQuote` beim Insert schreibt (ohne customerId/typeDigit). */
const CREATE_FIELDS = [
  'title', 'items', 'discountPercent', 'taxRate', 'validUntil',
  'introText', 'outroText', 'paymentTerms', 'details',
  'netTotal', 'grossTotal', 'status', 'quoteDate', 'sentAt', 'acceptedAt', 'rejectedAt',
] as const satisfies readonly (keyof CreateQuoteInput & keyof Quote)[];

type CreateField = (typeof CREATE_FIELDS)[number];

/**
 * Teilt Angebotsfelder in den Insert-Anteil und den Rest, den `createQuote`
 * nicht kennt (seit Summen/Status/Belegdaten im Insert gehen: praktisch leer).
 */
export const splitCreateQuoteFields = (
  updates: Partial<Quote>,
): { create: Partial<Pick<CreateQuoteInput, CreateField>>; rest: Partial<Quote> } => {
  const create: Partial<Pick<CreateQuoteInput, CreateField>> = {};
  const rest: Partial<Quote> = { ...updates };
  for (const k of CREATE_FIELDS) {
    if (updates[k] !== undefined) {
      (create as Record<CreateField, unknown>)[k] = updates[k];
    }
    delete rest[k];
  }
  return { create, rest };
};

/**
 * Vorlage in die Insert-Daten einarbeiten. Ohne Vorlage bleiben die
 * Basisdaten unveraendert und es gibt keine Summen nachzutragen.
 */
export const applyTemplateToCreateInput = (
  base: CreateQuoteInput,
  template: QuoteTemplate | null,
  newId: () => string = () => crypto.randomUUID(),
): { input: CreateQuoteInput; totals: { netTotal: number; grossTotal: number } | null } => {
  if (!template) return { input: base, totals: null };
  const p = template.payload;
  // Positionen mit frischen IDs (entkoppelt von der Vorlage).
  const items = (p.items || []).map((it) => ({ ...it, id: newId() }));
  const discountPercent = p.discountPercent ?? base.discountPercent;
  const taxRate = p.taxRate ?? base.taxRate;
  const totals = calculateQuoteTotals(items, discountPercent ?? 0, taxRate ?? 0);
  return {
    input: {
      ...base,
      title: p.title ?? base.title,
      introText: p.introText,
      outroText: p.outroText,
      paymentTerms: p.paymentTerms ?? base.paymentTerms,
      discountPercent,
      taxRate,
      items,
      details: {
        ...(base.details || {}),
        // Vorlage ohne eigene Sektionen -> blanko statt Fallback.
        sections: p.sections ?? [],
        ...(p.offerType ? { offerType: p.offerType } : {}),
      },
    },
    totals: { netTotal: totals.netTotal, grossTotal: totals.grossTotal },
  };
};
